"use client";

import { useEffect, useLayoutEffect, useState, type RefObject } from "react";
import type { Column, RowData } from "@tanstack/react-table";
import type { RecordGridFeatures } from "./record-grid-features";
import { ACTIONS_COLUMN_WIDTH, BORDER_SLACK } from "./grid-metrics";
import { useColumnWidths } from "./use-column-widths";

export function useContainerWidth(
  scrollRef: RefObject<HTMLDivElement | null>,
): number {
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    setContainerWidth(element.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [scrollRef]);

  return containerWidth;
}

export function useGridColumnLayout<TData extends RowData>({
  leafColumns,
  resizedWidths,
  containerWidth,
  hasRowActions,
  renderedWidthsRef,
}: {
  leafColumns: Column<RecordGridFeatures, TData, unknown>[];
  resizedWidths: Record<string, number>;
  containerWidth: number;
  hasRowActions: boolean;
  renderedWidthsRef: RefObject<Map<string, number>>;
}) {
  const actionsWidth = hasRowActions ? ACTIONS_COLUMN_WIDTH : 0;
  const available = containerWidth - actionsWidth - BORDER_SLACK;

  const columnWidths = useColumnWidths(leafColumns, resizedWidths, available);

  useEffect(() => {
    renderedWidthsRef.current = columnWidths;
  }, [columnWidths, renderedWidthsRef]);

  const columnsWidth = Array.from(columnWidths.values()).reduce(
    (total, width) => total + width,
    0,
  );
  const fillerWidth = Math.max(0, available - columnsWidth);
  const tableWidth = columnsWidth + fillerWidth + actionsWidth;

  return { columnWidths, fillerWidth, tableWidth };
}
